import { Button, Upload } from "@ews-admin/global-design-system";
import Papa from "papaparse";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { facilitiesApi } from "../../../services/facilities.api";
import { useAppDispatch } from "../../../store/hooks";
import { searchFacilitiesRequest } from "../../../store/reducers/facilitySlice";
import { FacilityType } from "../../../types/facility.definition";
import { AcceptedCountries } from "../../../types/user.definition";
import { Modal } from "../Modal/Modal";

const VALID_FACILITY_TYPES = Object.values(FacilityType);

type ParsedRow = {
  name: string;
  type: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  website?: string;
  accessInformation?: string;
  _errors: string[];
};

function validateRow(row: ParsedRow): string[] {
  const errs: string[] = [];
  if (!row.name?.trim()) errs.push("name");
  if (!row.email?.trim()) errs.push("email");
  if (!row.phone?.trim()) errs.push("phone");
  if (!row.address?.trim()) errs.push("address");
  if (!row.city?.trim()) errs.push("city");
  if (!row.state?.trim()) errs.push("state");
  // No zip check: Senegal and Chad do not use postal codes.
  if (
    !row.type?.trim() ||
    !VALID_FACILITY_TYPES.includes(
      row.type.trim().toUpperCase() as FacilityType
    )
  ) {
    errs.push("type");
  }
  return errs;
}

function parseRawRows(rows: Record<string, string>[]): ParsedRow[] {
  return rows
    .filter(r => Object.values(r).some(v => v?.trim()))
    .map(r => {
      const row: ParsedRow = {
        name: (r["name"] ?? r["Name"] ?? "").trim(),
        type: (r["type"] ?? r["Type"] ?? "").trim().toUpperCase(),
        email: (r["email"] ?? r["Email"] ?? "").trim(),
        phone: (r["phone"] ?? r["Phone"] ?? "").trim(),
        address: (r["address"] ?? r["Address"] ?? "").trim(),
        city: (r["city"] ?? r["City"] ?? "").trim(),
        state: (r["state"] ?? r["State"] ?? "").trim(),
        zip: (r["zip"] ?? r["Zip"] ?? r["ZIP"] ?? "").trim(),
        website: (r["website"] ?? r["Website"] ?? "").trim() || undefined,
        accessInformation:
          (r["accessInformation"] ?? r["access_information"] ?? "").trim() ||
          undefined,
        _errors: [],
      };
      row._errors = validateRow(row);
      return row;
    });
}

function downloadTemplate() {
  const headers = [
    "name",
    "type",
    "email",
    "phone",
    "address",
    "city",
    "state",
    "zip",
    "website",
    "accessInformation",
  ];
  const example = [
    "Hôpital Central",
    "HOSPITAL",
    "contact@hopital.sn",
    "+221770000001",
    "12 Rue Pasteur",
    "Dakar",
    "DAKAR",
    "10000",
    "https://hopital.sn",
    "",
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, example]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Facilities");
  XLSX.writeFile(wb, "facility_import_template.xlsx");
}

interface BulkFacilityImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BulkFacilityImportModal: React.FC<
  BulkFacilityImportModalProps
> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<{
    success: number;
    failed: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    if (isImporting) return;
    setParsedRows([]);
    setParseError(null);
    setImportSummary(null);
    onClose();
  };

  const processFile = useCallback(
    (file: File) => {
      setParseError(null);
      setParsedRows([]);
      setImportSummary(null);
      const name = file.name.toLowerCase();
      const MAX_MB = 5;
      if (file.size > MAX_MB * 1024 * 1024) {
        setParseError(
          t("admin.facilities.bulkImport.fileTooLarge", { max: MAX_MB })
        );
        return;
      }
      if (name.endsWith(".csv")) {
        Papa.parse<Record<string, string>>(file, {
          header: true,
          skipEmptyLines: true,
          complete: result => {
            if (result.errors.length > 0) {
              setParseError(t("admin.facilities.bulkImport.parseError"));
              return;
            }
            setParsedRows(parseRawRows(result.data));
          },
        });
      } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const wb = XLSX.read(e.target?.result, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, {
              defval: "",
            });
            setParsedRows(parseRawRows(rows));
          } catch {
            setParseError(t("admin.facilities.bulkImport.parseError"));
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        setParseError(t("admin.facilities.bulkImport.unsupportedFormat"));
      }
    },
    [t]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const hasErrors = parsedRows.some(r => r._errors.length > 0);

  const handleImport = async () => {
    if (parsedRows.length === 0 || hasErrors) return;
    setIsImporting(true);
    let success = 0;
    let failed = 0;
    for (const row of parsedRows) {
      const website =
        row.website &&
        !row.website.startsWith("http://") &&
        !row.website.startsWith("https://")
          ? `https://${row.website}`
          : row.website || null;
      try {
        await facilitiesApi.createFacility({
          name: row.name,
          facilityType: row.type as FacilityType,
          contactEmail: row.email,
          phoneNumber: row.phone,
          website,
          location: {
            address: row.address,
            city: row.city,
            state: row.state,
            zip: row.zip,
            accessInformation: row.accessInformation || undefined,
            country: AcceptedCountries.SENEGAL,
          },
          hours: [
            {
              monday: { open: "08:00", close: "18:00" },
              tuesday: { open: "08:00", close: "18:00" },
              wednesday: { open: "08:00", close: "18:00" },
              thursday: { open: "08:00", close: "18:00" },
              friday: { open: "08:00", close: "18:00" },
              saturday: { open: "09:00", close: "17:00" },
              sunday: { open: "10:00", close: "16:00" },
            },
          ],
          careTypeIds: [],
          specialtyIds: [],
          insurance: [],
          isOpen: true,
          isPartner: false,
          healthcareFacilityServices: [],
          photoUrl: "",
          rating: 0,
          reviews: 0,
        });
        success++;
      } catch {
        failed++;
      }
    }
    setIsImporting(false);
    setImportSummary({ success, failed });
    // MED-1468: a fully successful import used to close the dialog straight
    // away, which wiped the summary it had just set and left no trace of what
    // was created. Drop the preview instead — that hides the Import button so
    // the same file cannot be sent twice — and keep the summary on screen
    // until the user closes the dialog.
    setParsedRows([]);
    if (success > 0) {
      dispatch(
        searchFacilitiesRequest({
          keyword: "",
          offset: 0,
          limit: 10,
          sortBy: "name",
          sortOrder: "asc",
        })
      );
      toast.success(
        t("admin.facilities.bulkImport.importSummary", { success, failed })
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("admin.facilities.bulkImport.title")}
      size="lg"
    >
      <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
        {/* Instructions + template download */}
        <div className="flex items-start justify-between gap-4 rounded-lg bg-blue-50 border border-blue-100 p-4">
          <div className="text-sm text-blue-800 space-y-1">
            <p className="font-medium">
              {t("admin.facilities.bulkImport.instructions")}
            </p>
            <p className="text-xs text-blue-600">
              {t("admin.facilities.bulkImport.columns")}:{" "}
              <code className="font-mono">
                name, type, email, phone, address, city, state, zip, website,
                accessInformation
              </code>
            </p>
            <p className="text-xs text-blue-500">
              {t("admin.facilities.bulkImport.typeValues")}
            </p>
          </div>
          <button
            type="button"
            onClick={downloadTemplate}
            className="shrink-0 text-xs font-medium text-blue-700 underline hover:text-blue-900 whitespace-nowrap"
          >
            {t("admin.facilities.bulkImport.downloadTemplate")}
          </button>
        </div>

        {/* Import summary */}
        {importSummary && (
          <div
            className={`rounded-lg border p-4 text-sm ${
              importSummary.failed > 0
                ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                : "bg-green-50 border-green-200 text-green-800"
            }`}
          >
            {t("admin.facilities.bulkImport.importSummary", {
              success: importSummary.success,
              failed: importSummary.failed,
            })}
          </div>
        )}

        {/* Drop zone */}
        {parsedRows.length === 0 && !importSummary && (
          <div
            onDragOver={e => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 cursor-pointer transition-colors ${
              isDragging
                ? "border-primary-500 bg-primary-50"
                : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
            }`}
          >
            <Upload className="w-8 h-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">
              {t("admin.facilities.bulkImport.dropZone")}
            </p>
            <p className="text-xs text-gray-400">
              {t("admin.facilities.bulkImport.acceptedFormats")}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}

        {/* Parse error */}
        {parseError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {parseError}
          </div>
        )}

        {/* Preview table */}
        {parsedRows.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">
                {t("admin.facilities.bulkImport.preview", {
                  count: parsedRows.length,
                })}
              </p>
              <button
                type="button"
                onClick={() => {
                  setParsedRows([]);
                  setParseError(null);
                  setImportSummary(null);
                }}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                {t("admin.facilities.bulkImport.changeFile")}
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      #
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t("onboarding.facility.name")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t("onboarding.facility.type")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t("onboarding.facility.email")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t("onboarding.facility.phone")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t("onboarding.facility.city")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t("admin.facilities.bulkImport.statusCol")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {parsedRows.map((row, i) => (
                    <tr
                      key={i}
                      className={row._errors.length > 0 ? "bg-red-50" : ""}
                    >
                      <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                      <td
                        className={`px-3 py-2 ${row._errors.includes("name") ? "text-red-600 font-medium" : "text-gray-700"}`}
                      >
                        {row.name || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 ${row._errors.includes("type") ? "text-red-600 font-medium" : "text-gray-700"}`}
                      >
                        {row.type || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 ${row._errors.includes("email") ? "text-red-600 font-medium" : "text-gray-600"}`}
                      >
                        {row.email || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 ${row._errors.includes("phone") ? "text-red-600 font-medium" : "text-gray-600"}`}
                      >
                        {row.phone || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 ${row._errors.includes("city") ? "text-red-600 font-medium" : "text-gray-600"}`}
                      >
                        {row.city || "—"}
                      </td>
                      <td className="px-3 py-2">
                        {row._errors.length > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {t("admin.facilities.bulkImport.rowInvalid")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t("admin.facilities.bulkImport.rowValid")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasErrors && (
              <p className="text-xs text-red-600">
                {t("admin.facilities.bulkImport.rowErrors")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isImporting}
        >
          {importSummary ? t("common.close") : t("common.cancel")}
        </Button>
        {parsedRows.length > 0 && (
          <Button
            onClick={handleImport}
            loading={isImporting}
            disabled={isImporting || hasErrors || parsedRows.length === 0}
          >
            {isImporting
              ? t("admin.facilities.bulkImport.importing")
              : t("admin.facilities.bulkImport.submit", {
                  count: parsedRows.length,
                })}
          </Button>
        )}
      </div>
    </Modal>
  );
};
